'use client';

import { useTranslations } from 'next-intl';
import { CnoProShopifyMap } from 'content/products/addons/cnoPro';
import { CurrencySymbol, useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import { Currency } from 'utils/Currency';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { CustomImage } from '../custom-image';
import {
  Button,
  ImageContainer,
  InfoModalContainer,
  InfoModalDescription,
  InfoModalTitle,
  PricingModal,
} from './layout';
import { getAssetUrl } from 'lib/config/assetUrls';
import CustomLink from 'components/shared/CustomLink';
import { FSAHSAComponent } from '../HsaFsaComponent';
import useIsCnOPlus from 'hooks/pricing/useIsCnOPlus';
import styles from './CnoPro.module.css';

export const CnoProCartInfoModal = () => {
  const t = useTranslations('ring');
  const CartClient = useCartStore(
    useShallow((s) => ({ store: s.store, currency: s.currency })),
  );
  const { getValue } = useIsCnOPlus();
  const formatPriceWithComma = useFormatPrice();

  const { store, currency } = CartClient;
  const CurrencyCode = CurrencySymbol[currency ?? Currency.usd];

  const benefitsPro = [
    {
      title: t('ring.infoModals.cnoPro.benefitsPro.ovulationConfirmation.title'),
      description: t(
        'ring.infoModals.cnoPro.benefitsPro.ovulationConfirmation.p1',
      ),
    },
    {
      title: t('ring.infoModals.cnoPro.benefitsPro.anovulation.title'),
      description: t('ring.infoModals.cnoPro.benefitsPro.anovulation.p1'),
    },
    {
      title: t('ring.infoModals.cnoPro.benefitsPro.everyCycle.title'),
      description: t('ring.infoModals.cnoPro.benefitsPro.everyCycle.p1'),
    },
    {
      title: t('ring.infoModals.cnoPro.benefitsPro.cycleFlags.title'),
      description: t('ring.infoModals.cnoPro.benefitsPro.cycleFlags.p1'),
    },
    {
      title: t('ring.infoModals.cnoPro.benefitsPro.symptoms.title'),
      description: t('ring.infoModals.cnoPro.benefitsPro.symptoms.p1'),
    },
    {
      title: t('ring.infoModals.cnoPro.benefitsPro.predictions.title'),
      description: t('ring.infoModals.cnoPro.benefitsPro.predictions.p1'),
    },
    {
      title: t('ring.infoModals.cnoPro.benefitsPro.cycleReports.title'),
      description: t('ring.infoModals.cnoPro.benefitsPro.cycleReports.p1'),
    },
    {
      title: t('ring.infoModals.cnoPro.benefitsPro.globalStandards.title'),
      description: t('ring.infoModals.cnoPro.benefitsPro.globalStandards.p1'),
    },
    {
      title: t('ring.infoModals.cnoPro.benefitsPro.hsaFsa.title'),
      description: t('ring.infoModals.cnoPro.benefitsPro.hsaFsa.p1'),
    },
  ];

  const benefitsPlus = [
    {
      title: t('ring.infoModals.cnoPro.benefitsPlus.ovulationEstimation.title'),
      description: t(
        'ring.infoModals.cnoPro.benefitsPlus.ovulationEstimation.p1',
      ),
    },
    {
      title: t('ring.infoModals.cnoPro.benefitsPlus.unclearOvulation.title'),
      description: t(
        'ring.infoModals.cnoPro.benefitsPlus.unclearOvulation.p1',
      ),
    },
    {
      title: t('ring.infoModals.cnoPro.benefitsPlus.cycleFlags.title'),
      description: t('ring.infoModals.cnoPro.benefitsPlus.cycleFlags.p1'),
    },
    {
      title: t('ring.infoModals.cnoPro.benefitsPlus.symptoms.title'),
      description: t('ring.infoModals.cnoPro.benefitsPlus.symptoms.p1'),
    },
    {
      title: t('ring.infoModals.cnoPro.benefitsPlus.estimates.title'),
      description: t('ring.infoModals.cnoPro.benefitsPlus.estimates.p1'),
    },
    {
      title: t('ring.infoModals.cnoPro.benefitsPlus.cycleReports.title'),
      description: t('ring.infoModals.cnoPro.benefitsPlus.cycleReports.p1'),
    },
  ];

  const benefits = getValue(benefitsPro, benefitsPlus);
  const title = getValue(
    t('ring.infoModals.cnoPro.titlePro'),
    t('ring.infoModals.cnoPro.titlePlus'),
  );
  const altText = getValue('CNO Pro', 'CNO Plus');
  const description = getValue(
    t('ring.infoModals.cnoPro.descriptionPro'),
    t('ring.infoModals.cnoPro.descriptionPlus'),
  );

  const price =
    Math.floor(
      (CnoProShopifyMap[store as ShopifyStore].Year2.price / 2) * 100,
    ) / 100;

  return (
    <InfoModalContainer>
      <InfoModalTitle>
        <h3>{title}</h3>
        <p>
          <span>
            {t('ring.infoModals.cnoPro.startingAt')}
            <>
              {CurrencyCode}
              {formatPriceWithComma(price)}
            </>
            {t('ring.infoModals.cnoPro.perYear')}
          </span>
          <span>
            <FSAHSAComponent />
          </span>
        </p>
        <ImageContainer className="cno-pro-image-container">
          <CustomImage
            src={getValue(
              'web_v2/cart-info-modals/cno_pro_modal_hero.png',
              'web_v2/womens-health/plus/buy/cno-plus_modal.png',
            )}
            width={1376}
            height={808}
            alt={altText}
          />
        </ImageContainer>
      </InfoModalTitle>
      <InfoModalDescription>
        <p>{description}</p>
      </InfoModalDescription>
      <div className={styles.cnoProInfoDescription}>
        {benefits.map((benefit, index) => (
          <div key={index} className={styles.benefit}>
            <h3>{benefit.title}</h3>
            <p className={styles.description}>{benefit.description}</p>
          </div>
        ))}
      </div>
      <InfoModalDescription>
        <h3>{t('ring.infoModals.cnoPro.cycleReportsSection.title')}</h3>
        <p>{t('ring.infoModals.cnoPro.cycleReportsSection.p1')}</p>
        <ImageContainer className="cno-pro-image-container">
          <CustomImage
            src="web_v2/cart-info-modals/cno_pro_modal_report.png"
            width={1376}
            height={808}
            alt={altText}
          />
        </ImageContainer>
        <CustomLink
          rel="noopener noreferrer"
          href={getAssetUrl(
            getValue(
              '/web_v2/womens-health/documents/C&O_Pro_Sample%20Report.pdf',
              '/web_v2/womens-health/plus/documents/C&O_Plus_Sample_Report.pdf',
            ),
          )}
          target={'_blank'}
        >
          <Button>
            <span>{t('ring.infoModals.cnoPro.viewSampleReport')}</span>
          </Button>
        </CustomLink>
      </InfoModalDescription>
    </InfoModalContainer>
  );
};

export const CnoProPricingModal = () => (
  <PricingModal>
    <CnoProCartInfoModal />
  </PricingModal>
);
